import { useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import { Stack, Typography, Tooltip, IconButton } from "@financeable-com-au/financeable-ui";
import { getIsCommercialOrBusinessApplication } from "@/constants";
// @ts-expect-error: ignoring as it is existing js file
import { applicationSelector } from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  ApplicationType,
} from "@/store/slices/types/applicationFormSlice";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";

const DefaultRatesFees = () => {
  const {
    isSingleLenderProduct,
    lenderCard: { rate, loanAmount, financeAmount, brokerage, fees },
  } = useLenderCardContext();
  const typographySize = isSingleLenderProduct ? "body-sm" : "body-xs";
  // Get application information.
  const { applicationType } = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(applicationType);

  return (
    <Stack flexDirection="column" rowGap={2} flexGrow={1}>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography level="body-md" bold>
          Lender Rate
        </Typography>
        {rate.defaultRange ? (
          <Typography level="title-sm">
            {rate.defaultRange.min.formattedValue} - {rate.defaultRange.max.formattedValue}
          </Typography>
        ) : (
          <Typography level="title-md">{rate.default.formattedValue}</Typography>
        )}
      </Stack>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography level={typographySize} textColor="neutral.500">
          Effective Rate
        </Typography>
        {rate.effectiveRange ? (
          <Typography level={typographySize} bold>
            {rate.effectiveRange.min.formattedValue} - {rate.effectiveRange.max.formattedValue}
          </Typography>
        ) : (
          <Typography level={typographySize} bold>
            {rate.effective.formattedValue}
          </Typography>
        )}
      </Stack>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography level={typographySize} textColor="neutral.500">
          Comparison Rate
        </Typography>
        {rate.comparisonRange ? (
          <Typography level={typographySize} bold>
            {rate.comparisonRange.min.formattedValue} - {rate.comparisonRange.max.formattedValue}
          </Typography>
        ) : (
          <Typography level={typographySize} bold>
            {rate.comparison.formattedValue}
          </Typography>
        )}
      </Stack>
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Stack flexDirection="row" alignItems="center" justifyContent="flex-start">
          <Typography level={typographySize} textColor="neutral.500">
            Loan Amount
          </Typography>
          <Tooltip
            title={
              <Stack padding={2} flexDirection="column" rowGap={2}>
                <Typography>
                  Includes total financed fees of <Typography bold>{fees.totalFinanced.formattedValue}</Typography>
                </Typography>
              </Stack>
            }
          >
            <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
              <InfoIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography level={typographySize} bold>
          {loanAmount.formattedValue}
        </Typography>
      </Stack>
      {isCommercialOrBusinessApplication && (
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
          <Stack flexDirection="row" alignItems="center" justifyContent="flex-start">
            <Typography level={typographySize} textColor="neutral.500">
              Finance Amount
            </Typography>
            <Tooltip
              title={
                <Stack padding={2} flexDirection="column" rowGap={2}>
                  <Typography>
                    Includes brokerage amount of <Typography bold>{brokerage.amount.formattedValue}</Typography>
                  </Typography>
                </Stack>
              }
            >
              <IconButton aria-label="Tooltip" isRounded={true} size="sm" variant="inline">
                <InfoIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography level={typographySize} bold>
            {financeAmount.formattedValue}
          </Typography>
        </Stack>
      )}
      {applicationType !== ApplicationType.Personal && (
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
          <Typography level={typographySize} textColor="neutral.500">
            {isCommercialOrBusinessApplication ? "Brokerage" : "Commission"}
          </Typography>
          <Typography level={typographySize} bold>
            {brokerage.percentage.formattedValue} | {brokerage.amount.formattedValue}
          </Typography>
        </Stack>
      )}
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
        <Typography level={typographySize} textColor="neutral.500">
          Upfront / Financed Fees
        </Typography>
        <Typography level={typographySize} bold>
          {fees.totalUpfront.formattedValue} | {fees.totalFinanced.formattedValue}
        </Typography>
      </Stack>
    </Stack>
  );
};

export { DefaultRatesFees };
